import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { recognitionAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, getErrorMessage } from '@/lib/utils';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Sparkles, Settings, MapPin, CalendarDays, ArrowRight, Award } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const PROGRAM_STATUS_META = {
  coming_soon: { label: 'Coming Soon', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  qualification_open: { label: 'Qualification Open', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  selection: { label: 'Selection in Progress', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  confirmed: { label: 'Participants Confirmed', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  completed: { label: 'Completed', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  archived: { label: 'Archived', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

export const MY_STATUS_META = {
  invited: { label: "You're invited", cls: 'bg-amber-500 text-white' },
  confirmed: { label: 'Response received', cls: 'bg-blue-500 text-white' },
  pending_review: { label: 'Under review', cls: 'bg-orange-500 text-white' },
  approved: { label: 'Approved — Enter', cls: 'bg-emerald-600 text-white' },
  declined: { label: 'Declined', cls: 'bg-slate-400 text-white' },
  rejected: { label: 'Not selected', cls: 'bg-slate-400 text-white' },
};

export default function RecognitionPortalPage() {
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    recognitionAPI.listPrograms()
      .then((r) => setPrograms(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <PageHeader
        title="Events & Recognition"
        subtitle="Exclusive recognition programs and incentive experiences from KR Enterprise Group"
        actions={isKR && (
          <Button onClick={() => navigate('/recognition/admin')} data-testid="recognition-manage-btn">
            <Settings size={16} className="mr-2" /> Manage Programs
          </Button>
        )}
      />
      <div className="page-content">
        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin" size={30} /></div>
        ) : programs.length === 0 ? (
          <div className="text-center py-24 text-slate-500" data-testid="recognition-empty">
            <Award size={40} className="mx-auto mb-4 text-slate-300" />
            <p className="font-semibold text-slate-700">No recognition programs yet</p>
            <p className="text-sm">Upcoming programs will appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="recognition-grid">
            {programs.map((p) => {
              const meta = PROGRAM_STATUS_META[p.status] || PROGRAM_STATUS_META.coming_soon;
              const mine = p.my_status ? MY_STATUS_META[p.my_status] : null;
              return (
                <button
                  key={p.program_id}
                  onClick={() => navigate(`/recognition/${p.program_id}`)}
                  className="group text-left rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`recognition-card-${p.program_id}`}
                >
                  <div className="relative h-44 bg-gradient-to-br from-[#0f2547] to-[#1a4e88] overflow-hidden">
                    {p.cover_image_url ? (
                      <img src={`${BACKEND}${p.cover_image_url}`} alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/30"><Sparkles size={40} /></div>
                    )}
                    <div className="absolute top-3 left-3 flex gap-2">
                      <Badge className={`border ${meta.cls}`}>{meta.label}</Badge>
                    </div>
                    {mine && (
                      <div className="absolute top-3 right-3">
                        <Badge className={mine.cls}>{mine.label}</Badge>
                      </div>
                    )}
                    {!p.published && isKR && (
                      <div className="absolute bottom-3 left-3"><Badge variant="secondary">Draft</Badge></div>
                    )}
                  </div>
                  <div className="p-5">
                    {p.edition && <p className="text-[11px] font-semibold uppercase tracking-widest text-[#1a4e88] mb-1">{p.edition}</p>}
                    <h3 className="text-lg font-bold text-slate-900 leading-tight">{p.name}</h3>
                    {p.tagline && <p className="text-sm text-slate-500 italic mt-1">{p.tagline}</p>}
                    <div className="mt-3 space-y-1 text-xs text-slate-600">
                      {p.destination && <div className="flex items-center gap-1.5"><MapPin size={12} /> {p.destination}</div>}
                      {p.event_dates && <div className="flex items-center gap-1.5"><CalendarDays size={12} /> {p.event_dates}</div>}
                    </div>
                    <div className="mt-4 flex items-center text-sm font-semibold text-[#1a4e88] group-hover:gap-2 transition-all">
                      View details <ArrowRight size={15} className="ml-1" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
